import { Service } from "models/Service";
import { ApiClient } from "./utils/apiClient";
import { DataFormatter } from "./utils/dataFormatter";
import { MetaGenerator } from "./utils/metaGenerator";
import { mappings } from "./config/mappings";
import { BalotajePost } from './posts/balotaje';
import { getElectionPost } from './utils/postFactory';

export class Elections extends Service {
  constructor() {
    super({
      adsPath: "politica/nota",
      meta: {
        description: `Elecciones - Clarin.com`,
        keywords: 'Elecciones, Presidenciales, Gobernador, Resultados',
        title: `Elecciones - Clarin.com`,
        url: `elecciones`,
        extra: [],
      },
      section: "politica",
      sectionName: "Elecciones",
      sectionAds: "politica",
      template: "Elections",
      title: `Elecciones`,
      widthFull: true,
      slug: "elecciones-clarin",
      titleHeader: "Elecciones",
      titleHeaderLink: "/elecciones",
      autorefresh: false
    });
    
    this.apiClient = new ApiClient();
    this.dataFormatter = new DataFormatter();
    this.metaGenerator = new MetaGenerator();
  }

  parseData = async (query, menu) => {
    try {
      // 1. Parsear parámetros de URL
      let params = this.dataFormatter.parseUrlParams(query);
      const year = params.year || new Date().getFullYear();
      // 2. Obtener datos de la API
      const apiData = await this.apiClient.fetchElectionData(params);      
      // 3. Validar existencia de datos
      if (!apiData || !apiData.data || apiData.code === 404 || apiData.code === 500) {
        return this.handleDataNotFound(year, params);
      }

      let blocksNational = await this.apiClient.fetchBlocksNationalData(year, params?.typeElection);

      const apiUrlPres = `http://data-ecp.clarin.com/clarin.com/2023/argentina/${params?.electionInstance}/AR_president.json`;
      let infoPresidentOrGovernors = await this.apiClient.fetchData(apiUrlPres);

      params.infoPresidentOrGovernors = infoPresidentOrGovernors;
      params.blocksNational = blocksNational || [];

      // 4. Validar que los datos tengan estructura mínima
      if (!this.validateApiData(apiData.data, params)) {
        return this.handleDataNotFound(year, params);
      }
      
      // 5. Generar post según tipo de elección
      let postData;
      let postGenerator;
      try {
        // Tu parseData ya está correcto:
        if (params.electionType === 'balotaje' && params.typeElection === 'presidente') {
          postGenerator = new BalotajePost();
        } else {
          postGenerator = getElectionPost(params.typeElection);
        }
        postData = await postGenerator.generate({
          apiData: apiData.data,
          params,
          mappings,
          menu
        });
      } catch (generatorError) {
        console.error("Error al generar post:", generatorError);
        return this.handleDataNotFound(year, params);
      }
      
      // 6. Validar que se generó contenido
      if (!postData || !postData.content) {
        return this.handleDataNotFound(year, params);
      }
      
      // 7. Generar metadatos
      const meta = this.metaGenerator.generate(postData, params);
      
      // 8. Configurar respuesta exitosa
      this.setData(postData);
      this.setTitle(postData.content?.title || `Elecciones ${year}`);
      this.setTitleHeader("Elecciones");
      this.setMeta(this.parseMeta(meta));
      
    } catch (error) {
      console.error("Error crítico en parseData:", error);
      const year = query[1] || new Date().getFullYear();
      const params = { typeElection: query[3] || 'general' };
      return this.handleDataNotFound(year, params);
    }
  }

  /**
   * Validar que los datos de la API tengan estructura mínima requerida
   */
  validateApiData(data, params) {
    if (!data) return false;
    
    // Validaciones básicas
    if (!data.name && !data.districts) return false;
    
    // Validaciones específicas por tipo de elección
    if (params.typeElection === 'legislativas') {
      return data.districts && Array.isArray(data.districts);
    }
    
    // Para presidente y gobernador necesitamos parties
    if (['presidente', 'gobernador', 'jefeGobierno'].includes(params.typeElection)) {
      return data.parties && Array.isArray(data.parties) && data.parties.length > 0;
    }
    
    return true;
  }

  /**
   * Manejar casos donde no hay datos disponibles (404)
   */
  handleDataNotFound(year, params) {
    const electionTypeNames = {
      'presidente': 'Presidenciales',
      'gobernador': 'Gobernador',
      'jefeGobierno': 'Jefe de Gobierno',
      'legislativas': 'Legislativas',
      'delegados': 'Delegados Comunales',
      'senadores': 'Senadores',
      // 'diputados': 'Diputados',
      // 'concejales': 'Concejales',
      // 'intendentes': 'Intendentes'
    };

    const electionTypeName = electionTypeNames[params.typeElection] || 'Elecciones';
    const title = `Elecciones ${electionTypeName} ${year}`;

    this.setData({ code: 404 });
    this.setTitle(title);
    this.setTitleHeader(title);
    this.setMeta(this.parseMeta({
      description: `Resultados de las ${title}`,
      title: title,
      keywords: `${electionTypeName}, Elecciones ${year}, Argentina, Resultados`,
      url: this.url,
      extra: [
        { name: "title", content: title },
        { name: "description", content: `Resultados de las ${title}` },
        { name: "distribution", content: "global" },
        { name: "robots", content: "noindex, nofollow" },
        { property: "og:title", content: title },
        { property: "og:type", content: "website" },
        { property: "article:published_time", content: `${year}-10-22T19:05:00.000Z` },
        { property: "article:modified_time", content: new Date().toISOString() },
      ]
    }));

    return this;
  }
}